import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SEO, BreadcrumbSchema } from '../components/SEO';

export default function AccessibilityPage() {
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Website Accessibility', path: '/website-accessibility' }
  ];

  return (
    <div className="min-h-screen" data-testid="accessibility-page">
      <SEO 
        title="Website Accessibility Statement - RenoNovo"
        description="RenoNovo is committed to ensuring digital accessibility for people with disabilities. Read our WCAG 2.1 Level AA accessibility statement."
        keywords="accessibility statement, WCAG, ADA compliance, website accessibility, RenoNovo"
        canonical="/website-accessibility"
      />
      <BreadcrumbSchema items={breadcrumbs} />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-8 md:pt-40 md:pb-12 bg-[#f8fafc]">
          <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
            <h1 className="text-4xl sm:text-5xl font-extrabold text-[#0f172a] tracking-tight leading-tight mb-4">
              Website Accessibility Statement
            </h1>
            <p className="text-[#64748b]">Last Updated: February 27, 2026</p>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-12 md:py-16">
          <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="prose prose-lg max-w-none">
              
              {/* Our Commitment */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-[#0f172a] mb-4">Our Commitment to Accessibility</h2>
                <p className="text-[#64748b] leading-relaxed">
                  We are committed to ensuring digital accessibility for people with disabilities. We are continually 
                  improving the user experience for everyone and applying relevant accessibility standards to enhance 
                  usability and inclusivity. Our goal is to provide a website that is accessible to the widest possible 
                  audience, regardless of technology or ability.
                </p>
              </div>

              {/* Accessibility Standards */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-[#0f172a] mb-4">Accessibility Standards</h2>
                <p className="text-[#64748b] leading-relaxed">
                  We strive to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA, which are widely 
                  recognized as the industry standard for website accessibility. Accessibility is an ongoing process, and 
                  we are actively working toward improving the accessibility and usability of our website.
                </p>
              </div>

              {/* Accessibility Technology & Tools */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-[#0f172a] mb-4">Accessibility Technology & Tools</h2>
                <p className="text-[#64748b] leading-relaxed mb-4">
                  To support our accessibility efforts, we have deployed industry-standard accessibility technology 
                  designed to enhance usability and assist visitors with disabilities. These tools may provide features such as:
                </p>
                <ul className="list-disc list-inside text-[#64748b] space-y-2 ml-4">
                  <li>Screen reader enhancements</li>
                  <li>Keyboard navigation improvements</li>
                  <li>Color contrast adjustments</li>
                  <li>Text size and spacing controls</li>
                  <li>Focus indicators</li>
                  <li>Additional accessibility interface options</li>
                </ul>
                <p className="text-[#64748b] leading-relaxed mt-4">
                  This technology helps us improve accessibility while we continue broader auditing, remediation, 
                  and development efforts.
                </p>
              </div>

              {/* Ongoing Improvement */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-[#0f172a] mb-4">Ongoing Improvement</h2>
                <p className="text-[#64748b] leading-relaxed mb-4">
                  Accessibility is an ongoing initiative. We are:
                </p>
                <ul className="list-disc list-inside text-[#64748b] space-y-2 ml-4">
                  <li>Reviewing site content and functionality regularly</li>
                  <li>Working with developers and accessibility tools</li>
                  <li>Identifying and remediating barriers</li>
                  <li>Incorporating accessibility into future design and development</li>
                </ul>
                <p className="text-[#64748b] leading-relaxed mt-4">
                  We understand accessibility is a continuous journey and are committed to making improvements over time.
                </p>
              </div>

              {/* Third-Party Content */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-[#0f172a] mb-4">Third-Party Content</h2>
                <p className="text-[#64748b] leading-relaxed">
                  Some areas of the website may include third-party content, plugins, or integrations that are not fully 
                  controlled by us. While we encourage vendors to provide accessible content, we cannot guarantee 
                  accessibility of third-party features. However, we welcome feedback regarding any issues encountered.
                </p>
              </div>

              {/* Feedback & Assistance */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-[#0f172a] mb-4">Feedback & Assistance</h2>
                <p className="text-[#64748b] leading-relaxed mb-4">
                  If you experience difficulty accessing any part of this website, need assistance, or would like to 
                  request content in an alternative format, please contact us.
                </p>
                <p className="text-[#64748b] leading-relaxed mb-4">Please include:</p>
                <ul className="list-disc list-inside text-[#64748b] space-y-2 ml-4">
                  <li>The page URL</li>
                  <li>Description of the issue</li>
                  <li>Your preferred contact method</li>
                </ul>
                <p className="text-[#64748b] leading-relaxed mt-4">
                  <strong className="text-[#0f172a]">Contact:</strong> Information on our{' '}
                  <a href="/contact" className="text-[#2563eb] hover:underline">Contact Us Page</a>
                </p>
                <p className="text-[#64748b] leading-relaxed mt-4">
                  We aim to respond within a reasonable timeframe and will make reasonable efforts to provide 
                  accessible alternatives.
                </p>
              </div>

              {/* Good Faith Efforts */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-[#0f172a] mb-4">Good Faith Efforts</h2>
                <p className="text-[#64748b] leading-relaxed">
                  We are making good-faith efforts to comply with accessibility standards and improve usability for 
                  individuals with disabilities. Despite our efforts, some content may not yet fully meet the strictest 
                  accessibility standards due to the evolving nature of technology and ongoing updates. We appreciate 
                  your understanding as we continue improving accessibility.
                </p>
              </div>

              {/* Terms & Conditions */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-[#0f172a] mb-4">Terms & Conditions — Accessibility Clause</h2>
                <p className="text-[#64748b] leading-relaxed">
                  We are committed to providing an accessible website experience for all users and strive to follow 
                  recognized accessibility standards, including WCAG guidelines. We have implemented industry-standard 
                  accessibility tools and continue to review, update, and improve our website to enhance accessibility. 
                  Because accessibility is an ongoing process, some areas of the website may not yet fully conform to 
                  all accessibility standards. If you encounter accessibility barriers, please contact us so we can 
                  provide assistance, offer alternative access, and address the issue where feasible. Nothing in these 
                  Terms is intended to limit any rights available under applicable accessibility laws.
                </p>
              </div>

            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
